import React, { useState, useEffect } from "react";
import { pdfjs, Document, Page } from 'react-pdf';
import { Alert, Box, CircularProgress } from '@mui/joy';
import { Anchor } from "../Typography";
import { useTranslation } from 'react-i18next';
import { Button } from "../Button";
import { isDocumentMediaPath } from 'src/mtska-frontend-data-provider/redux/downloadDocumentMedia';
import { resolvePublicMediaUrl, useResolvedMediaSrc } from 'src/mtska-frontend-data-provider/hooks/useResolvedMediaSrc';

pdfjs.GlobalWorkerOptions.workerSrc = '/worker/pdf.worker.min.mjs';

const WidgetDocumentPreview = ({ item, name, downloadable = true }) => {
    const { t } = useTranslation();
    const [url, setUrl] = useState();
    const [numPages, setNumPages] = useState();
    const [pageNumber, setPageNumber] = useState(1);
    const [hasPdfError, setHasPdfError] = useState(false);

    const isObject = (value) => {
        return typeof value === 'object' && value !== null && !Array.isArray(value);
    };
    const fieldValue = (name && item && Object.prototype.hasOwnProperty.call(item, name)) ? item[name] : undefined;
    const shouldWarnInvalidType = Boolean(name && fieldValue !== undefined && fieldValue !== null && !isObject(fieldValue));
    const shouldErrorInvalidObject = Boolean(name && fieldValue !== undefined && isObject(fieldValue) && (!fieldValue.url || !fieldValue.extension));
    const resolved = isObject(fieldValue) ? fieldValue : (isObject(item) ? item : undefined);

    const mediaUrl = typeof url === 'string' && url && isDocumentMediaPath(url) ? url : null;
    const { src, ready, loading: mediaLoading, error: mediaError, isMediaPath, downloadHref } = useResolvedMediaSrc(mediaUrl);

    useEffect(() => {
        if (resolved) {
            setUrl(resolved.url ?? false);
        } else {
            setUrl(false);
        }
    }, [resolved?.url]);

    useEffect(() => {
        setHasPdfError(false);
        setNumPages(undefined);
        setPageNumber(1);
    }, [url]);

    useEffect(() => {
        if (mediaError) {
            setHasPdfError(true);
        }
    }, [mediaError]);

    const onDocumentLoadSuccess = (document) => {
        setHasPdfError(false);
        setNumPages(document._pdfInfo.numPages);
    }

    const onDocumentLoadFail = () => {
        setHasPdfError(true);
    }

    const onDocumentLoadError = () => {
        setHasPdfError(true);
    }

    const handlePreviousPage = () => {
        setPageNumber(prevPage => Math.max(prevPage - 1, 1));
    };

    const handleNextPage = () => {
        setPageNumber(prevPage => Math.min(prevPage + 1, numPages));
    };

    const resolvePreviewSource = () => {
        if (!url || url === false) {
            return null;
        }
        if (url.startsWith('blob:')) {
            return url;
        }
        if (isMediaPath) {
            return ready ? src : null;
        }
        return resolvePublicMediaUrl(url);
    };

    const previewSource = resolvePreviewSource();
    const downloadLinkHref = isMediaPath && downloadHref
        ? downloadHref
        : (url ? resolvePublicMediaUrl(url) : null);

    const showMediaLoading = isMediaPath && mediaLoading;
    const showPreviewUnavailable = isMediaPath && !mediaLoading && !previewSource;

    return (
        (shouldWarnInvalidType || url === false) ? (
            <Box className="form-element form-element-preview-widget col-12">
                <Box sx={{ padding: '10px', border: '1px solid rgba(255, 161, 161, 0.35)', borderRadius: '8px', textAlign: 'center' }}>
                    {t('No document to preview', { name })}
                </Box>
            </Box>
        ) : shouldErrorInvalidObject ? (
            <Box className="form-element form-element-preview-widget col-12">
                <Box sx={{ padding: '10px', border: '1px solid rgba(255, 161, 161, 0.35)', borderRadius: '8px', textAlign: 'center' }}>
                    {t('Invalid document object', { name })}
                </Box>
            </Box>
        ) : url ? (
            <Box className="form-element form-element-preview-widget col-12">
                {
                    downloadable && downloadLinkHref && (
                        <Anchor href={downloadLinkHref} target="_blank" level="body-small" icon="faDownload" className={"download_document"}>{t('Download')}</Anchor>
                    )
                }
                {
                    showMediaLoading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, width: '100%' }}>
                            <Alert
                                variant="soft"
                                color="neutral"
                                sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}
                                startDecorator={<CircularProgress size="sm" />}
                            >
                                {t('Loading document')}
                            </Alert>
                        </Box>
                    )
                }
                {
                    resolved?.extension === 'pdf' && url && !showMediaLoading && (
                        <Box className='preview-pdf'>
                            {showPreviewUnavailable ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, width: '100%' }}>
                                    <Alert variant="soft" color="warning" sx={{ width: '100%' }}>
                                        {t('Unable to load document preview')}
                                    </Alert>
                                </Box>
                            ) : previewSource && (
                                <Document file={previewSource}
                                    onLoadSuccess={onDocumentLoadSuccess}
                                    onLoadFail={onDocumentLoadFail}
                                    onError={onDocumentLoadError}
                                    error={
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, width: '100%' }}>
                                            <Alert variant="soft" color="warning" sx={{ width: '100%' }}>
                                                {t('Unable to load document preview')}
                                            </Alert>
                                        </Box>
                                    }
                                    loading={
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, width: '100%' }}>
                                            <Alert
                                                variant="soft"
                                                color="neutral"
                                                sx={{ alignItems: 'center', justifyContent: 'center', width: '100%' }}
                                                startDecorator={<CircularProgress size="sm" />}
                                            >
                                                {t('Loading document')}
                                            </Alert>
                                        </Box>
                                    }
                                >
                                    <Page
                                        pageNumber={pageNumber}
                                        renderAnnotationLayer={false}
                                        renderTextLayer={false}
                                    />
                                </Document>
                            )}

                            {
                                !hasPdfError && numPages > 0 && (
                                    <Box className="paginator">
                                        <Button onClick={handlePreviousPage} disabled={pageNumber <= 1} icon="faArrowLeft" className={'paginator-previous'}>
                                            {t('Previous')}
                                        </Button>

                                        <span className="paginator-info">{t('Page {{pageNumber}} of {{numPages}}', { pageNumber: pageNumber, numPages: numPages })} </span>
                                        <Button onClick={handleNextPage} disabled={pageNumber >= numPages} icon="faArrowRight" className={'paginator-next'}>
                                            {t('Next')}
                                        </Button>
                                    </Box>
                                )
                            }
                        </Box>
                    )
                }
                {
                    ['png', 'jpg', 'jpeg'].includes(resolved?.extension) && !showMediaLoading && (
                        <Box className='preview-image'>
                            {showPreviewUnavailable ? (
                                <Alert variant="soft" color="warning" sx={{ width: '100%' }}>
                                    {t('Unable to load document preview')}
                                </Alert>
                            ) : previewSource && (
                                <img src={previewSource} width={500} alt="" />
                            )}
                        </Box>
                    )
                }
            </Box>
        ) : null
    );
}

export default WidgetDocumentPreview;
